/**
 * A text box with a filtered listbox under it, and where that listbox is drawn.
 *
 * `StatisticMenu` built this twice over for one control — the placement, the
 * keyboard handling, the rule that the box shows the chosen label whatever the
 * list below currently holds — and the second control that needs all three is
 * the League menu on Add Source. Fifty-four leagues over ten publishers is past
 * what a `<select>` can answer: what a native menu offers instead of a search is
 * the browser's own type-ahead, which matches from the **start** of the label
 * and resets after a second, so finding NCAA Division II means already knowing
 * whether it is filed under N or under D — and finding which *source* publishes
 * it is not a question a `<select>` can be asked at all.
 *
 * So the parts that were expensive to get right move here, and the two controls
 * share them rather than drifting:
 *
 * - **`usePlacement`**, which is the one with real measurements behind it. See
 *   its own comment; it was driven in a browser against a clipped scroll
 *   container and the answer is not guessable.
 * - **`Combobox`**, the box, the list, the keys and the ARIA wiring.
 *
 * **The data stays outside.** `Combobox` is handed rows that are already
 * filtered and holds the query as a controlled prop, so what *matching* means
 * lives in a module with no React in it — `stat-search.ts` for one control and
 * `league-menu.ts` for the other — and `tests/` can assert it under bare node.
 * A filter inside this file would be a filter nothing could test.
 *
 * **Nothing about the search decides what is stored.** It narrows the rows
 * offered and nothing else: no row is auto-selected, an exact match is not
 * committed on blur, and a term matching nothing leaves the control holding
 * exactly what it held. That is the rule `StatisticMenu` states at length and
 * the reason both controls are text boxes rather than `<select>`s — a `<select>`
 * whose value matches none of its options renders blank, which is this
 * frontend's most-repeated defect.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import { cx } from "./ui";
import { CheckIcon } from "./icons";

/** Where a menu is drawn, in viewport coordinates. */
export interface Placement {
  left: number;
  width: number;
  /** One of these, never both: `bottom` is how a menu opening upward is pinned
   *  to the control without having to know its own height first. */
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/**
 * Where a listbox opened from `anchor` can be drawn.
 *
 * **Fixed rather than absolute, and that is the whole reason this exists.** The
 * field table is wrapped in `TableScroll`, which is `overflow-x-auto` — and CSS
 * computes `overflow-y: visible` to `auto` the moment the other axis is not
 * visible, so the wrapper clips vertically although nothing in its class list
 * says so. Driven in a browser on the last row of a table: an absolutely
 * positioned menu leaves **392 statistics as a sliver of one line**, and turns
 * the wrapper into a 573px scroll container inside a 265px frame. The rows a
 * reviewer reaches last are the ones it ruins. `Modal` escapes the same
 * clipping the same way, so this is the stylesheet's existing answer rather
 * than a new one.
 *
 * Measured on open and again on every scroll and resize while open, because a
 * fixed element does not travel with the row it belongs to. The scroll listener
 * is in the **capture** phase: what moves under this menu is almost never the
 * window but an ancestor — the horizontal table scroller, the page — and a
 * scroll on an element does not bubble.
 *
 * It opens upward when there is not room below and more room above, and is held
 * inside the viewport horizontally, so the last row of a table in a short
 * window still gets a usable list.
 *
 * Moved here from `StatisticMenu`, byte for byte, when the League menu on
 * Add Source became the second control needing it. One copy, because the
 * measurements behind it are not recoverable from reading either caller — the
 * default width floor of 280 and the eight-pixel viewport margin in particular
 * are answers to a browser rather than to an argument.
 */
export function usePlacement(
  anchor: RefObject<HTMLElement>,
  open: boolean,
  /**
   * The narrowest the menu may be, when the anchor is narrower still.
   *
   * 280 was measured against a row holding a label and a check mark. A caller
   * that puts something else on every row — `StatisticMenu`'s sport badge —
   * spends part of that floor on the badge and truncates the label it is there
   * to let people read, so it raises the floor rather than paying for the badge
   * out of the statistic's name.
   */
  minWidth = 280,
  /**
   * The tallest the menu may be where there is room for it.
   *
   * 320 suits a panel that is a status line over a list of rows. A caller that
   * draws a third thing in the same panel raises this rather than taking the
   * height out of the list — `StatisticMenu`'s detail panel sits under its
   * rows, and the rows are what `field-mapping-usability/01` exists to show.
   * Still only a ceiling: the space actually measured below the control is what
   * gets used, so a menu near the foot of the window is the size it always was,
   * and the League menu, which passes nothing, is unchanged at 320.
   */
  maxTall = 320,
): Placement | null {
  const [placement, setPlacement] = useState<Placement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }

    const measure = () => {
      const box = anchor.current?.getBoundingClientRect();
      if (!box) return;

      // The gap between control and menu, and the margin the menu keeps from
      // the edge of the window.
      const gap = 4;
      const margin = 8;
      const below = window.innerHeight - box.bottom - gap - margin;
      const above = box.top - gap - margin;
      // Only when there is genuinely not room: flipping on a near-tie would
      // move the menu from one keystroke to the next.
      const upward = below < 200 && above > below;
      const width = Math.min(Math.max(box.width, minWidth), window.innerWidth - margin * 2);

      setPlacement({
        left: Math.max(margin, Math.min(box.left, window.innerWidth - margin - width)),
        width,
        top: upward ? undefined : box.bottom + gap,
        bottom: upward ? window.innerHeight - box.top + gap : undefined,
        // A floor as well as a ceiling. A menu three rows tall is not worth
        // opening, and `overflow-y` inside it means the floor costs nothing
        // but a little overlap with the control in a very short window.
        maxHeight: Math.max(160, Math.min(maxTall, upward ? above : below)),
      });
    };

    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [anchor, open, minWidth, maxTall]);

  return placement;
}

/** One row of a combobox's list. */
export interface ComboboxRow {
  value: string;
  label: string;
  /**
   * A second line under the label, for a fact the label cannot carry. The
   * League menu's is the sport and the publishers — which is the whole question
   * somebody opened the menu with, so it is a line of its own rather than a
   * title attribute nobody hovers to read.
   */
  note?: string;
  /** A short mark at the right of the row. Absent on most rows by design: a
   *  badge on every one is a badge saying nothing. */
  badge?: string;
}

/**
 * A searchable menu over `rows`.
 *
 * `rows` arrive **already filtered by `query`**, which the caller also owns.
 * That split is what keeps the matching rule testable: `league-menu.ts` and
 * `stat-search.ts` hold no React and run under bare node, and a filter written
 * inside this component would be a filter nothing could assert.
 *
 * `total` is how many rows there were before filtering, so the count line can
 * say *12 of 54* rather than only *12*. A list that does not say what it is a
 * list of leaves the reader unable to tell a narrow search from a small menu.
 */
export function Combobox({
  value,
  chosenLabel,
  rows,
  total,
  query,
  onQueryChange,
  onChange,
  label,
  placeholder,
  searchPlaceholder = "Type to search…",
  noun,
  empty,
  compact = false,
  disabled = false,
}: {
  /** The chosen row's `value`, or "" for none. */
  value: string;
  /**
   * What the shut box should say, looked up by the caller against the
   * **unfiltered** list. `rows` is filtered, so it cannot always answer this.
   */
  chosenLabel?: string;
  rows: readonly ComboboxRow[];
  /**
   * How many rows there were before the *query* narrowed them — but after any
   * facet the caller applies, because the facet is a different kind of thing.
   * A sport filter changes what this menu is a menu *of*; the query searches
   * within that. A count line reading "2 of 54 match" under a filter that left
   * twenty on screen is describing a list nobody is looking at.
   */
  total: number;
  query: string;
  onQueryChange: (query: string) => void;
  onChange: (value: string) => void;
  /** What a screen reader calls this box. */
  label: string;
  /** What the box says when nothing is chosen. */
  placeholder: string;
  searchPlaceholder?: string;
  /** Singular noun for the count line: "league", "source". */
  noun: string;
  /** What to say when the search matched nothing. */
  empty?: ReactNode;
  compact?: boolean;
  disabled?: boolean;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const placement = usePlacement(anchor, open);
  const searching = query.trim() !== "";
  const chosen = rows.find((row) => row.value === value) ?? null;
  /**
   * What the shut box displays.
   *
   * Three sources in order, and the middle one is why the prop exists. `rows`
   * is **filtered** — by the caller's query, and on Add Source by a sport facet
   * too — so a control holding a value that is not in the list it is currently
   * not showing must still be able to say what it holds. Reading only `rows`
   * would blank the box, which is the `<select>` defect this component was
   * factored out to prevent, reached from the other direction.
   *
   * `chosenLabel` is the caller's authoritative answer, looked up against the
   * unfiltered list. Falling back to the raw `value` after it is the last
   * resort and shows a key rather than a name — ugly on purpose, because a box
   * that silently went blank would be worse and a caller that reaches it has a
   * bug worth seeing.
   */
  const shutLabel = chosen?.label ?? chosenLabel ?? (value !== "" ? value : "");

  // A new search is read from the top.
  useEffect(() => setActive(0), [query]);

  // The active row scrolled to, rather than merely highlighted. Arrowing into a
  // row nobody can see is how a keyboard user loses a long list.
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, active, rows]);

  function show() {
    if (open || disabled) return;
    onQueryChange("");
    // Opening onto the row already chosen, so a menu opened by mistake is
    // closed by Enter without changing anything.
    const index = rows.findIndex((row) => row.value === value);
    setActive(index < 0 ? 0 : index);
    setOpen(true);
  }

  function hide() {
    setOpen(false);
    onQueryChange("");
  }

  function choose(row: ComboboxRow) {
    onChange(row.value);
    hide();
    box.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        show();
        return;
      }
      if (rows.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + rows.length) % rows.length);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      if (!open || rows.length === 0) return;
      event.preventDefault();
      setActive(event.key === "Home" ? 0 : rows.length - 1);
      return;
    }
    if (event.key === "Enter") {
      // Only when the menu is open, and then it is this control's key rather
      // than the form's — a row chosen here must not also submit the form
      // around it.
      if (!open) return;
      event.preventDefault();
      const row = rows[active];
      if (row) choose(row);
      return;
    }
    if (event.key === "Escape") {
      if (!open) return;
      event.preventDefault();
      // So a menu open inside a dialog closes the menu rather than the dialog.
      event.stopPropagation();
      hide();
      return;
    }
    // Tab leaves the control, and leaving it is not choosing anything.
    if (event.key === "Tab") hide();
  }

  return (
    // Closed by clicking anywhere outside, which for a control whose only
    // focusable part is the box is exactly "the box lost focus". The rows
    // refuse focus on mousedown, so clicking one does not blur first and close
    // the list out from under the click.
    <div
      ref={anchor}
      className="relative min-w-0"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hide();
      }}
    >
      <input
        ref={box}
        type="text"
        role="combobox"
        className={cx("input combobox w-full", compact && "input-sm")}
        aria-label={label}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={open && rows[active] ? `${id}-row-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        placeholder={open ? searchPlaceholder : placeholder}
        // The chosen label whenever the menu is shut, whatever the list holds.
        // While it is open the box is the search term.
        value={open ? query : shutLabel}
        // So the first keystroke after tabbing in replaces the label rather
        // than being appended to it.
        onFocus={() => box.current?.select()}
        onMouseDown={show}
        onChange={(event) => {
          onQueryChange(event.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />

      {open && placement && (
        <div
          className={cx(
            "fixed z-50 flex flex-col overflow-hidden rounded-md",
            "border border-hairline bg-canvas shadow-overlay",
          )}
          style={{
            left: placement.left,
            width: placement.width,
            top: placement.top,
            bottom: placement.bottom,
            maxHeight: placement.maxHeight,
          }}
        >
          {/* How much of the menu is on screen, which is the question a long
              list raises and a scrollbar answers badly. Announced as well as
              shown: a term typed into a box whose list is elsewhere tells a
              screen reader nothing by itself. */}
          <div
            role="status"
            aria-live="polite"
            className="shrink-0 border-b border-hairline px-sm py-xxs text-caption text-muted-soft"
          >
            {searching
              ? `${rows.length} of ${total} match “${query.trim()}”`
              : `${total} ${total === 1 ? noun : `${noun}s`} · type to search`}
          </div>

          <ul
            ref={list}
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            className="m-0 min-h-0 flex-1 list-none overflow-y-auto overscroll-contain p-0"
          >
            {rows.map((row, index) => (
              <li
                key={row.value}
                id={`${id}-row-${index}`}
                role="option"
                aria-selected={row.value === value}
                data-active={index === active}
                className={cx(
                  "flex cursor-pointer items-start justify-between gap-xs px-sm py-xs text-caption",
                  index === active && "bg-surface-soft",
                )}
                // Keeps focus in the box, so the click that follows lands on a
                // menu that is still open.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(row)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink">{row.label}</span>
                  {row.note && (
                    <span className="mt-xxs block truncate text-muted-soft">{row.note}</span>
                  )}
                </span>
                {row.badge && (
                  <span className="shrink-0 pt-xxs text-muted-soft">{row.badge}</span>
                )}
                {row.value === value && <CheckIcon size={14} className="mt-xxs shrink-0" />}
              </li>
            ))}
          </ul>

          {rows.length === 0 && (
            <div className="shrink-0 px-sm py-sm text-caption text-muted">
              {empty ?? <>Nothing matches “{query.trim()}”.</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
